import {
  ExclamationCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import BTUBubbles from 'components/common/btu-bubbles';
import Tooltip from 'components/layout/tooltip';
import {
  BTUsType,
  GridObjectType,
  GridSpaceDataType,
  LineSetType,
  ZoneOutlineData
} from 'data/types';
import { useMemo } from 'react';
import { findBestLabelPosition } from 'utils/zone-label-placement';
import {
  computePerimeter,
  createPerimeterPath,
  formatAirHandlerDescriptor,
  getZonePhotoCenter,
  ZONE_LABEL_HEIGHT_NO_BTUS,
  ZONE_LABEL_HEIGHT_WITH_BTUS,
  ZONE_LABEL_WIDTH
} from 'utils/zone-outline';
import ProductPhoto, {
  PRODUCT_PHOTO_HEIGHT,
  PRODUCT_PHOTO_WIDTH
} from './product-photo';

type ValidationStatus = Readonly<{
  isValid: boolean;
  hasWarning: boolean;
  messages: string[];
}>;

type ZoneLabelProps = Readonly<{
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
  placement: 'top' | 'bottom' | 'left' | 'right';
  name: string;
  BTUs?: BTUsType;
  color: string;
  validationStatus?: ValidationStatus;
}>;

function getLabelOffset(
  placement: ZoneLabelProps['placement'],
  anchor: ZoneLabelProps['anchor'],
  hasBTUs: boolean
): { x: number; y: number } {
  const labelWidth = ZONE_LABEL_WIDTH;
  const labelHeight = hasBTUs
    ? ZONE_LABEL_HEIGHT_WITH_BTUS
    : ZONE_LABEL_HEIGHT_NO_BTUS;

  let xOffset = 0;
  if (anchor === 'middle') xOffset = -labelWidth / 2;
  else if (anchor === 'end') xOffset = -labelWidth;

  let yOffset = 0;
  if (placement === 'top') yOffset = -labelHeight;
  else if (placement === 'bottom') yOffset = 0;
  else yOffset = -labelHeight / 2;

  return { x: xOffset, y: yOffset };
}

function getAlignItems(
  anchor: ZoneLabelProps['anchor']
): 'center' | 'flex-end' | 'flex-start' {
  if (anchor === 'middle') return 'center';
  if (anchor === 'end') return 'flex-end';
  return 'flex-start';
}

function ZoneLabel({
  x,
  y,
  anchor,
  placement,
  name,
  BTUs,
  color,
  validationStatus
}: ZoneLabelProps) {
  const hasBTUs = !!BTUs;
  const hasError = validationStatus && !validationStatus.isValid;
  const hasWarning =
    validationStatus && validationStatus.isValid && validationStatus.hasWarning;
  const hasValidationIssue = hasError || hasWarning;
  const labelWidth = ZONE_LABEL_WIDTH + (hasValidationIssue ? 24 : 0);
  const labelHeight = hasBTUs
    ? ZONE_LABEL_HEIGHT_WITH_BTUS
    : ZONE_LABEL_HEIGHT_NO_BTUS;
  const offset = getLabelOffset(placement, anchor, hasBTUs);

  const messages = validationStatus?.messages ?? [];

  return (
    <foreignObject
      x={x + offset.x}
      y={y + offset.y}
      width={labelWidth}
      height={labelHeight}
      style={{
        overflow: 'visible',
        pointerEvents: hasValidationIssue ? 'auto' : 'none'
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: getAlignItems(anchor),
          gap: '2px',
          userSelect: 'none'
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            position: 'relative'
          }}
        >
          <span
            style={{
              color,
              fontSize: '16px',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {name}
          </span>
          {hasError && (
            <ExclamationCircleIcon
              style={{
                width: '18px',
                height: '18px',
                color: '#ef4444',
                flexShrink: 0
              }}
            />
          )}
          {hasWarning && (
            <ExclamationTriangleIcon
              style={{
                width: '18px',
                height: '18px',
                color: '#f59e0b',
                flexShrink: 0
              }}
            />
          )}
          {hasValidationIssue && messages.length > 0 && (
            <Tooltip text={messages.length === 1 ? messages[0] : undefined}>
              {messages.length > 1 ? (
                <ul className='list-disc pl-4'>
                  {messages.map((msg) => (
                    <li key={msg}>{msg}</li>
                  ))}
                </ul>
              ) : undefined}
            </Tooltip>
          )}
        </div>
        {BTUs && <BTUBubbles BTUs={BTUs} abbreviateNumbers />}
      </div>
    </foreignObject>
  );
}

type ZoneOutlineProps = Readonly<{
  zoneData: ZoneOutlineData;
  allGridSpaces: GridSpaceDataType[];
  objects?: GridObjectType[];
  lineSets?: LineSetType[];
  levelUniqueId?: string;
  validationStatus?: ValidationStatus;
  isHovered?: boolean;
  onHover?: (zoneId: string | null) => void;
}>;

export const AIR_HANDLER_PHOTO_WIDTH = PRODUCT_PHOTO_WIDTH;
export const AIR_HANDLER_PHOTO_HEIGHT = PRODUCT_PHOTO_HEIGHT;

export default function ZoneOutline({
  zoneData,
  allGridSpaces,
  objects = [],
  lineSets = [],
  levelUniqueId,
  validationStatus,
  isHovered = false,
  onHover
}: ZoneOutlineProps) {
  const {
    zoneName,
    BTUs,
    spacePositions,
    spaceShapes,
    color,
    airHandlerPhotoUrl,
    airHandlerName,
    airHandlerSize,
    airHandlerType
  } = zoneData;

  const displayAirHandlerName = formatAirHandlerDescriptor(airHandlerName);
  const photoTooltipParts = [airHandlerSize, airHandlerType]
    .map(formatAirHandlerDescriptor)
    .filter((part): part is string => typeof part === 'string');

  const airHandlerPhotoCenter = useMemo(
    () => getZonePhotoCenter(zoneData, allGridSpaces),
    [zoneData, allGridSpaces]
  );

  const perimeterPaths = useMemo(() => {
    if (spaceShapes.length === 0) return [];
    return computePerimeter(spaceShapes);
  }, [spaceShapes]);

  const pathString = useMemo(
    () => createPerimeterPath(perimeterPaths, 6),
    [perimeterPaths]
  );

  const hasBTUs = BTUs.heating > 0 || BTUs.cooling > 0;

  const computedLabelPosition = useMemo(
    () =>
      findBestLabelPosition(
        spaceShapes,
        allGridSpaces,
        8,
        objects,
        lineSets,
        hasBTUs,
        spacePositions,
        [],
        levelUniqueId
      ),
    [
      spaceShapes,
      allGridSpaces,
      objects,
      lineSets,
      hasBTUs,
      spacePositions,
      levelUniqueId
    ]
  );

  const labelPosition = zoneData.labelPosition ?? computedLabelPosition;

  if (spaceShapes.length === 0 || perimeterPaths.length === 0) {
    return null;
  }

  const displayName = zoneName;

  const fillColor = color.outline;
  const strokeColor = color.outline;
  const labelColor = color.text;

  const getFillOpacity = () => (isHovered ? 0.35 : 0.15);

  const getOpacity = () => (isHovered ? 1 : 0.9);

  return (
    <g
      className='zone-outline'
      style={{
        pointerEvents: onHover ? 'auto' : 'none'
      }}
      onMouseEnter={() => onHover?.(zoneData.zoneId)}
      onMouseLeave={() => onHover?.(null)}
    >
      <path
        id={`zone-outline-${zoneData.zoneId}`}
        d={pathString}
        fill={fillColor}
        fillOpacity={getFillOpacity()}
        stroke={strokeColor}
        strokeWidth={isHovered ? 6 : 4}
        strokeLinecap='round'
        strokeLinejoin='round'
        opacity={getOpacity()}
        style={{
          transition: 'fill-opacity 150ms, stroke-width 150ms, opacity 150ms'
        }}
      />
      <ZoneLabel
        x={labelPosition.x}
        y={labelPosition.y}
        anchor={labelPosition.anchor}
        placement={labelPosition.placement}
        name={displayName}
        BTUs={hasBTUs ? BTUs : undefined}
        color={labelColor}
        validationStatus={validationStatus}
      />
      {airHandlerPhotoUrl && airHandlerPhotoCenter && (
        <ProductPhoto
          uniqueId={`zone-photo-${zoneData.zoneId}`}
          center={airHandlerPhotoCenter}
          photoUrl={airHandlerPhotoUrl}
          strokeColor={fillColor}
          name={displayAirHandlerName}
          descriptors={photoTooltipParts}
        />
      )}
    </g>
  );
}
